import { useEffect, useState } from "react";

import { Button, Colors } from "@blueprintjs/core";
import { useNavigate, useParams } from "react-router";

import { isProgramLead } from "@/components/helpers/workspace/permissions";
import { Box, Flex } from "@/components/layout/flexbox";
import { Nav } from "@/components/nav/Nav";
import { AddProgramComponentDialog } from "@/components/pages_logged_in/program/components/AddProgramComponentDialog";
import { CloseProgramComponentDialog } from "@/components/pages_logged_in/program/components/CloseProgramComponentDialog";
import {
  COMPONENT_DETAILS_PATHS,
  scrollWithOffset,
} from "@/components/pages_logged_in/program/components/component-utils";
import {
  isComponentInProgressOrOverdue,
  isComponentUpcomingOrSoon,
} from "@/components/pages_logged_in/program/components/componentMetadata";
import { DeleteProgramComponentDialog } from "@/components/pages_logged_in/program/components/DeleteProgramComponentDialog";
import { ComponentList } from "@/components/pages_logged_in/program/components/ProgramDashboardFeed";
import { ProgramDetailsLoader } from "@/components/pages_logged_in/program/components/ProgramDetailsLoader";
import { DETAILS_PROGRAM_TABS } from "@/components/pages_logged_in/program/pages/program-details/constants";
import { PermissionErrorPage } from "@/components/pages_public/403";
import { HEADER_HEIGHT } from "@/components/pieces/menu/MenuHeader";
import {
  ComponentTypeEnum,
  useProgramComponentsQueryLazyQuery,
} from "@/graphql";
import {
  ComponentDialog,
  LeadComponent,
  MainComponent,
  MainProgram,
  ParticipantComponent,
} from "@/types/programs";
import { ensureNonNullArray } from "@/utils/array";

import { getSortedComponents } from "./utils";

type ProgramComponent = MainComponent | LeadComponent | ParticipantComponent;

const getEmptyText = (view: string) => {
  const viewData = DETAILS_PROGRAM_TABS.find((tab) => tab.key === view);
  if (viewData) {
    return {
      emptyText: `No ${viewData.label} Yet`,
      buttonText: `Add ${viewData.label}`,
    };
  }
  return {
    emptyText: "No Surveys Yet",
    buttonText: "Add Survey",
  };
};

interface ProgramSurveysProps {
  view: string;
  program: MainProgram;
  refetchProgram: () => void;
}

export function ProgramSurveys({ view, program }: ProgramSurveysProps) {
  const { id } = useParams();
  const [initialLoad, setInitialLoad] = useState(false);
  const isLead = isProgramLead(id);
  const navigate = useNavigate();
  const hashData = window.location.hash.slice(1);

  const [fetchComponent, { data: componentsData, loading: loadingComponent }] =
    useProgramComponentsQueryLazyQuery({
      onCompleted: (data) => {
        if (data?.programComponents && !initialLoad) {
          scrollWithOffset({
            componentTag: hashData,
            offset: -(HEADER_HEIGHT + 30),
          });
          setInitialLoad(true);
        }
      },
    });

  useEffect(() => {
    if (isLead && id) {
      fetchComponent({ variables: { programId: id } });
    }
  }, [id, isLead]);

  useEffect(() => {
    if (hashData) {
      // setTimeout to wait render components
      const timeout = setTimeout(() => {
        scrollWithOffset({
          componentTag: hashData,
          offset: -(HEADER_HEIGHT + 30),
        });
      }, 500);
      return () => clearTimeout(timeout);
    }
  }, [hashData]);

  const filterComponentType = () => {
    if (view === "action-items") {
      return [ComponentTypeEnum.ActionItem];
    }
    if (view === "surveys") {
      return [ComponentTypeEnum.Survey];
    }
    if (view === "ai-roleplays") {
      return [ComponentTypeEnum.AiRoleplay];
    }
    if (view === "videos") {
      return [ComponentTypeEnum.Video];
    }
    if (view === "articles") {
      return [ComponentTypeEnum.Article];
    }
    if (view === "announcement") {
      return [ComponentTypeEnum.Announcement];
    }
    if (view === "group-meetings") {
      return [ComponentTypeEnum.GroupMeeting];
    }

    return [
      ComponentTypeEnum.Survey,
      ComponentTypeEnum.ActionItem,
      ComponentTypeEnum.AiRoleplay,
      ComponentTypeEnum.Video,
      ComponentTypeEnum.Article,
      ComponentTypeEnum.GroupMeeting,
    ];
  };

  const types = filterComponentType();
  const componentType = types.length === 1 ? types[0] : undefined;

  const leadsComponents = ensureNonNullArray(
    componentsData?.programComponents?.leadsComponents,
  );

  const sortedComponents = getSortedComponents({
    components: leadsComponents,
  }).filter((component) => component.type && types.includes(component.type));

  const upcomingComponents = sortedComponents.filter((component) =>
    isComponentUpcomingOrSoon(component.status),
  );

  const onGoingComponents = sortedComponents.filter((component) =>
    isComponentInProgressOrOverdue(component.status),
  );

  const completedComponents = sortedComponents.filter(
    (component) => component.status === "Completed",
  );

  const inDesignComponents = sortedComponents.filter(
    (component) => component.status === "InDesign",
  );

  const [selectedComponent, setSelectedComponent] =
    useState<ProgramComponent>();
  const [dialogOpen, setDialogOpen] = useState<ComponentDialog>();

  const isProgramClosed = Boolean(program?.closedAt);

  const handleEditComponent = (component: ProgramComponent) => {
    setSelectedComponent(component);
    setDialogOpen("edit-component");
  };

  const handleDeleteComponent = (component: ProgramComponent) => {
    setSelectedComponent(component);
    setDialogOpen("delete-component");
  };

  const handleCloseComponent = (component: ProgramComponent) => {
    setSelectedComponent(component);
    setDialogOpen("close-component");
  };

  const closeDialog = () => {
    setDialogOpen(undefined);
    setSelectedComponent(undefined);
  };

  const hasCompletedData = completedComponents?.length > 0;
  const hasUpcomingData = upcomingComponents?.length > 0;
  const hasIndesignData = inDesignComponents?.length > 0;
  const hasOngingData = onGoingComponents?.length > 0;

  if (!program || (loadingComponent && !componentsData)) {
    return (
      <ProgramDetailsLoader
        includeSidebar={true}
        hasHeader={false}
        sizeNavWidth={0}
      />
    );
  }
  if ((!isLead || !program) && !loadingComponent)
    return <PermissionErrorPage />;

  const { emptyText, buttonText } = getEmptyText(view);

  return (
    <Flex width="100%" flexDirection={["column", "column", "column", "row"]}>
      <Nav />
      <Box
        flex={1}
        mt={
          (hasUpcomingData || hasCompletedData) &&
          !hasOngingData &&
          !hasIndesignData
            ? 0
            : 4
        }
      >
        <>
          {!sortedComponents?.length && (
            <Flex
              css={{
                background: Colors.WHITE,
                borderBottom: `1px solid ${Colors.LIGHT_GRAY2}`,
                minHeight: 200,
              }}
              width="100%"
              py="24px"
              justifyContent="center"
              alignItems="center"
              flexDirection="column"
            >
              <Box my={2}>{emptyText}</Box>
              {!isProgramClosed && (
                <Button
                  intent="primary"
                  onClick={() => {
                    setDialogOpen("add-component");
                  }}
                >
                  {buttonText}
                </Button>
              )}
            </Flex>
          )}
          <ComponentList
            role="lead"
            large={true}
            components={inDesignComponents}
            onEdit={handleEditComponent}
            onDelete={handleDeleteComponent}
            onClose={handleCloseComponent}
            program={program}
          />
          <ComponentList
            role="lead"
            large={true}
            components={onGoingComponents}
            onEdit={handleEditComponent}
            onDelete={handleDeleteComponent}
            onClose={handleCloseComponent}
            program={program}
          />

          {hasUpcomingData && (
            <Box mt={40}>
              {(hasIndesignData || hasOngingData) && (
                <Box className="bbs" my={40} />
              )}
              <Box fontSize={20} fontWeight={600} mb={3} mt={40}>
                Looking Forward
              </Box>
            </Box>
          )}

          <ComponentList
            role="lead"
            large={true}
            components={upcomingComponents}
            onEdit={handleEditComponent}
            onDelete={handleDeleteComponent}
            onClose={handleCloseComponent}
            program={program}
          />

          {hasCompletedData && (
            <Box mt={40}>
              {(hasIndesignData || hasOngingData || hasUpcomingData) && (
                <Box className="bbs" />
              )}
              <Box fontSize={20} fontWeight={600} mb={3} mt={40}>
                Complete
              </Box>
            </Box>
          )}

          <ComponentList
            role="lead"
            large={true}
            components={completedComponents}
            onEdit={handleEditComponent}
            onDelete={handleDeleteComponent}
            onClose={handleCloseComponent}
            program={program}
          />

          {selectedComponent && (
            <>
              <DeleteProgramComponentDialog
                component={selectedComponent}
                programId={program.id}
                open={dialogOpen === "delete-component"}
                onClose={() => {
                  setDialogOpen(undefined);
                  setSelectedComponent(undefined);
                }}
              />

              <CloseProgramComponentDialog
                component={selectedComponent}
                programId={program.id}
                open={dialogOpen === "close-component"}
                onClose={() => {
                  setDialogOpen(undefined);
                  setSelectedComponent(undefined);
                }}
                onCloseComponent={() => {
                  if (selectedComponent?.id && program?.id) {
                    const path =
                      selectedComponent?.type &&
                      COMPONENT_DETAILS_PATHS[selectedComponent?.type];
                    navigate(
                      `/programs/${program?.id}/${path}/${selectedComponent?.id}`,
                    );
                  }
                }}
              />
            </>
          )}
        </>
      </Box>
      <AddProgramComponentDialog
        // TODO: Update logic to surveys only if new type of components was implemented or support edit surveys
        componentId={selectedComponent?.id}
        programId={program.id}
        open={dialogOpen === "edit-component" || dialogOpen === "add-component"}
        onClose={closeDialog}
        componentType={componentType}
      />
    </Flex>
  );
}
